// ---------- touches de peinture : une touche de pinceau par case, couchée le long des formes ----------
//   0        case vide, du papier nu (l'encodeur n'en émet jamais : une toile n'a pas de réserve)
//   1..24    vingt-quatre couleurs, une par code, dans l'ordre de PAINTS
//
// D'OÙ VIENT CET ALPHABET. Quatre planches d'essai (2026-10) ont fait tourner une quarantaine
// d'alphabets sur huit tableaux et neuf visages serrés ; celui-ci est le premier à avoir plu
// franchement. Mesuré sur le banc (OKLab ×100, feuille et original floutés d'une case) : 1,9 à
// 3,0 sur les visages à 150 cases, la fidélité de nos meilleurs alphabets.
//
// CE QUI LE DISTINGUE : L'ANGLE DE LA TOUCHE NE VIENT PAS DU CODE. Chaque touche suit le contour
// le plus proche, comme un peintre qui modèle une joue. Mettre l'angle dans le code aurait coûté
// 24 couleurs × 4 sens = 96 pastilles, injouable, et des paliers de 45° qui cassent les courbes.
// L'angle se recalcule donc depuis la GRILLE SOLUTION, que le jeu possède dès l'ouverture : la
// clarté de chaque couleur, floutée d'une case (le tramage ne doit pas faire de faux contours),
// puis un tenseur de structure lissé sur cinq cases. Mesuré sur le banc : l'image obtenue est
// indiscernable de celle dont l'angle vient de la photo (2,91 contre 3,08 sur Vermeer, 1,68
// contre 1,69 sur Frida). Rien ne s'ajoute au puzzle ni au serveur, et rien ne bouge en cours de
// partie : l'angle dépend de la solution, pas de ce qui est déjà peint.
//
// LA TOUCHE DÉBORDE de sa case (~0,6 case), et c'est voulu : c'est ce qui fond la grille en
// tableau. L'ordre de pose décide donc qui recouvre qui : il est imposé (orderedPlay), et la
// couche figée rejoue les cases dans l'ordre des taps (cf. drawBaseOn dans src/render.ts).
// Pas de pose par-dessus la couche (bakesOnTop), qui suppose un dessin qui reste dans sa case.
// L'image de feuille des gestes (sheetMode), elle, se dessine dans l'ordre des taps comme la
// couche figée : mesuré le 2026-10-14, image en mouvement contre image à l'arrêt, 0,01/255
// d'écart moyen, aucun pixel à plus de 24 ; un pincement dézoomé passe de 436 ms à 50 par
// image (103×120, processeur ×4).

import type { Alphabet } from './types';

/**
 * La palette, apprise sur le corpus puis retouchée à la main.
 *
 * Seize viennent d'un k-means en OKLab sur les huit images de test, avec six ancres imposées
 * (noir, blanc cassé, rouge, jaune, vert, bleu) : sans elles le ciel du *Cri* sortait brun, la
 * moyenne mange les teintes franches minoritaires. Le k-means sortait aussi trois noirs presque
 * identiques (#000001, #050202, #15120f) ; deux sont remplacés par ce qui manquait aux ombres —
 * un bleu nuit et une terre brûlée, les deux ombres colorées d'un portrait.
 *
 * LES SIX DERNIÈRES (codes 19 à 24) comblent les trous du cercle des teintes. Mesurés en OKLab,
 * les dix-huit premières laissaient 122° vides entre le bleu nuit et le rouge (ni violet, ni
 * rose) et 109° entre le vert et le bleu ciel (ni turquoise, ni bleu canard), sans orange franc,
 * vert sombre ni rouge sombre. Banc d'essai sur 17 sujets à 120 cases : l'écart moyen passe de
 * 2,52 à 2,42 — modeste en moyenne, mais 2,68 → 1,91 sur la statue de la Liberté (le vert-de-gris
 * du cuivre) et une barbe enfin orange chez Van Gogh. Trente couleurs ne gagnaient plus que
 * 2,31 pour six pastilles de plus à chercher dans la barre.
 *
 * Ce qu'il ne fallait PAS faire, mesuré aussi : échanger trois gris contre ces teintes, à nombre
 * égal. L'écart montait à 2,63 — un tableau tient par ses valeurs avant ses teintes, et les
 * neuf neutres échelonnés en clarté sont ce qui le modèle.
 *
 * Ajoutées en fin de liste plutôt qu'intercalées : un code garde son sens, donc une grille
 * convertie avec dix-huit couleurs se dessine toujours juste. L'ordre de la barre, lui, est
 * réglé à part (paletteOrder).
 */
export const PAINTS: readonly { css: string; name: string }[] = [
  { css: '#15120f', name: 'noir d’ivoire' },
  { css: '#1f2c4f', name: 'bleu nuit' },
  { css: '#362e25', name: 'terre d’ombre' },
  { css: '#8a3b24', name: 'terre de Sienne brûlée' },
  { css: '#534d3f', name: 'gris olive' },
  { css: '#2c62ad', name: 'bleu' },
  { css: '#c42f26', name: 'rouge' },
  { css: '#3f7f3a', name: 'vert' },
  { css: '#726d5b', name: 'gris chaud' },
  { css: '#858c82', name: 'gris vert' },
  { css: '#c19270', name: 'chair' },
  { css: '#74adeb', name: 'bleu ciel' },
  { css: '#a3b6a6', name: 'vert de gris' },
  { css: '#e9b92e', name: 'jaune' },
  { css: '#bacdd3', name: 'gris bleu' },
  { css: '#ffc088', name: 'chair claire' },
  { css: '#d6e2e7', name: 'blanc bleuté' },
  { css: '#f6f1e6', name: 'blanc' },
  { css: '#6a3d8f', name: 'violet' },
  { css: '#d4507a', name: 'rose' },
  { css: '#2a9d8f', name: 'turquoise' },
  { css: '#e07b2a', name: 'orange' },
  { css: '#24452b', name: 'vert sombre' },
  { css: '#6b1f2a', name: 'bordeaux' },
];

/** Le papier de la feuille (--paper-rgb) : ce qu'on voit entre deux touches, et ce sur quoi
 *  l'encodeur mesure chaque couleur. */
export const PAPER = '#fbf6ec';

// ---------- OKLab, le strict nécessaire ----------
const s2l = (c: number): number => {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};
const l2s = (c: number): number => {
  c = Math.max(0, Math.min(1, c));
  return 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
};
export function srgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const R = s2l(r);
  const G = s2l(g);
  const B = s2l(b);
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}
function oklabToCss(L: number, a: number, b: number): string {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const R = l2s(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s);
  const G = l2s(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s);
  const B = l2s(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s);
  return `rgb(${Math.round(R)},${Math.round(G)},${Math.round(B)})`;
}
export function hexRgb(h: string): [number, number, number] {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}
/** Décale la clarté d'une couleur en OKLab, teinte gardée. */
function shade(hex: string, k: number): string {
  const [L, a, b] = srgbToOklab(...hexRgb(hex));
  return oklabToCss(Math.max(0, Math.min(1, L + k)), a, b);
}

/** Une couleur et ses teintes dérivées : le haut de la touche, le bas, et les poils. */
export interface Tone {
  css: string;
  light: string;
  dark: string;
  bristle: string;
}

/** Les teintes d'une couleur, à calculer une fois par couleur et non par case : `draw` tourne
 *  20 000 fois par reconstruction et une conversion OKLab par appel y coûtait plus que le dessin.
 *  Exportée pour `peintureNuancee`, qui peint avec la même touche dans un autre nuancier. */
export function toneOf(css: string): Tone {
  return { css, light: shade(css, 0.07), dark: shade(css, -0.08), bristle: shade(css, -0.1) };
}

const TONES = PAINTS.map(({ css }) => toneOf(css));

/** Clarté OKLab de chaque code, pour le champ d'angles (indice = code). */
const CODE_L = [1, ...PAINTS.map(({ css }) => srgbToOklab(...hexRgb(css))[0])];

/** Le même hachage que le banc d'essai : le tremblé d'une touche est figé par sa case. */
function hash2(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function boxBlur(a: Float32Array, W: number, H: number, r: number): Float32Array {
  const t = new Float32Array(W * H);
  const o = new Float32Array(W * H);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      let s = 0;
      let n = 0;
      for (let k = Math.max(0, x - r); k <= Math.min(W - 1, x + r); k++, n++) s += a[y * W + k];
      t[y * W + x] = s / n;
    }
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      let s = 0;
      let n = 0;
      for (let k = Math.max(0, y - r); k <= Math.min(H - 1, y + r); k++, n++) s += t[k * W + x];
      o[y * W + x] = s / n;
    }
  return o;
}

/**
 * L'angle de la touche de chaque case, depuis la seule grille de codes.
 *
 * Un tenseur de structure : pour chaque case le gradient de clarté (gx, gy), puis la moyenne
 * locale de gx², gy² et gx·gy, dont l'orientation dominante est 0,5·atan2(2C, A − B). On prend la
 * perpendiculaire : la touche longe le contour au lieu de le traverser. Moyenner le TENSEUR et non
 * les angles est ce qui rend la chose stable — deux gradients opposés (les deux flancs d'un nez)
 * s'annulent en angles mais s'additionnent en tenseur, puisqu'ils décrivent le même bord.
 *
 * Exportée pour le test : un bord vertical doit donner des touches verticales. `codeL` est la
 * clarté de chaque code, celle de ce nuancier par défaut — `peintureNuancee` passe la sienne.
 */
export function strokeAngles(
  target: readonly (readonly number[])[],
  codeL: readonly number[] = CODE_L,
): Float32Array {
  const gh = target.length;
  const gw = gh ? target[0].length : 0;
  const L = new Float32Array(gw * gh);
  for (let r = 0; r < gh; r++)
    for (let c = 0; c < gw; c++) L[r * gw + c] = codeL[target[r][c]] ?? 1;
  const Ls = boxBlur(L, gw, gh, 1);
  const A = new Float32Array(gw * gh);
  const B = new Float32Array(gw * gh);
  const C = new Float32Array(gw * gh);
  for (let y = 1; y < gh - 1; y++)
    for (let x = 1; x < gw - 1; x++) {
      const i = y * gw + x;
      const dx = Ls[i + 1] - Ls[i - 1];
      const dy = Ls[i + gw] - Ls[i - gw];
      A[i] = dx * dx;
      B[i] = dy * dy;
      C[i] = dx * dy;
    }
  const bl = (t: Float32Array) => boxBlur(boxBlur(t, gw, gh, 2), gw, gh, 2);
  const As = bl(A);
  const Bs = bl(B);
  const Cs = bl(C);
  const out = new Float32Array(gw * gh);
  for (let i = 0; i < gw * gh; i++)
    out[i] = 0.5 * Math.atan2(2 * Cs[i], As[i] - Bs[i]) + Math.PI / 2;
  return out;
}

/** L'angle d'une touche hors de toute grille (pastille de palette, particules) : une diagonale
 *  douce, celle d'un droitier. */
export const DEFAULT_ANGLE = -0.45;

/**
 * Le champ d'angles de la grille courante, pour un nuancier donné (`codeL`, la clarté de chaque
 * code). Une fabrique et non un état du module depuis que `peintureNuancee` peint avec la même
 * touche : chaque alphabet garde SA grille courante, et la vignette de l'un ne vient pas
 * remplacer la feuille ouverte de l'autre.
 *
 * Une WeakMap par grille et non un seul champ : la vignette d'un autre puzzle se dessine pendant
 * qu'une feuille est ouverte, et recalculer le champ de la feuille à chaque aller-retour coûterait
 * ~10 ms à chaque fois.
 */
export function angleField(codeL: readonly number[]) {
  const fields = new WeakMap<object, Float32Array>();
  let cur: {
    field: Float32Array;
    gw: number;
    gh: number;
    ox: number;
    oy: number;
  } | null = null;
  let curTarget: object | null = null;
  return {
    prepare(target: readonly (readonly number[])[], originX = 0, originY = 0): void {
      if (target === curTarget && cur && cur.ox === originX && cur.oy === originY) return;
      let field = fields.get(target);
      if (!field) {
        field = strokeAngles(target, codeL);
        fields.set(target, field);
      }
      curTarget = target;
      cur = { field, gw: target[0]?.length ?? 0, gh: target.length, ox: originX, oy: originY };
    },
    at(x: number, y: number, w: number, h: number): number {
      if (!cur) return DEFAULT_ANGLE;
      const gx = Math.round((x - cur.ox) / w);
      const gy = Math.round((y - cur.oy) / h);
      if (gx < 0 || gy < 0 || gx >= cur.gw || gy >= cur.gh) return DEFAULT_ANGLE;
      return cur.field[gy * cur.gw + gx];
    },
  };
}

const angles = angleField(CODE_L);

/**
 * Le dégradé d'une touche, UN par couleur et par largeur au lieu d'un par case. Il est posé dans
 * le repère de la touche (déjà translaté et tourné), donc il ne dépend que de la couleur et de W,
 * et toutes les cases d'une feuille ont le même W.
 *
 * Mesuré sur un Pixel 7a le 2026-10-18 (2 500 touches de 15 px, médiane de six passes) : 70 ms
 * avec un dégradé neuf par touche, 52 en cache — à pixels identiques, écart nul. Un quart du
 * temps d'une feuille en peinture partait dans createLinearGradient et addColorStop.
 * Remplacer save/restore par getTransform/setTransform, essayé dans la même passe, ne gagnait rien.
 *
 * Un CanvasGradient n'appartient à aucun contexte : celui de la feuille sert aussi à son image en
 * cache et aux vignettes. Une seule largeur retenue par couleur — la feuille et ses images en
 * cache dessinent toutes en unités monde, donc au même W ; seule une vignette en change.
 */
const gradients = new WeakMap<Tone, { W: number; g: CanvasGradient }>();
function gradientOf(ctx: CanvasRenderingContext2D, t: Tone, W: number): CanvasGradient {
  let e = gradients.get(t);
  if (!e || e.W !== W) {
    const g = ctx.createLinearGradient(0, -W, 0, W);
    g.addColorStop(0, t.light);
    g.addColorStop(0.5, t.css);
    g.addColorStop(1, t.dark);
    e = { W, g };
    gradients.set(t, e);
  }
  return e.g;
}

/** Une touche de couleur `t` dans la case (x, y, w, h). Exportée pour `peintureNuancee` : les
 *  deux alphabets doivent peindre exactement la même touche, réglages compris (largeur 0,80). */
export function stroke(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  t: Tone,
  angle: number,
): void {
  const s = Math.min(w, h);
  // la position de la case, et pas un compteur : le tremblé d'une touche ne doit pas changer
  // d'un dessin à l'autre de la même case (couche reconstruite, vignette, partage)
  const gx = Math.round(x / w);
  const gy = Math.round(y / h);
  const seed = gx * 977 + gy * 13;
  const ang = angle + (hash2(seed, 3) - 0.5) * 0.5;
  ctx.save();
  ctx.translate(
    x + w / 2 + (hash2(seed, 4) - 0.5) * s * 0.3,
    y + h / 2 + (hash2(seed, 5) - 0.5) * s * 0.3,
  );
  ctx.rotate(ang);
  // RIEN SOUS LA TOUCHE, et c'est sa largeur qui couvre la toile.
  //
  // Elle posait d'abord un fond carré dans sa case — la préparation brune réchauffée de la couleur
  // — pour boucher les coins qu'une touche penchée laisse au papier. Feuille pleine, invisible :
  // les touches voisines le recouvrent. Mais on ne joue pas une feuille pleine, on la remplit case
  // par case, et une case seule sortait en carré presque noir sous sa touche — « bizarre » au
  // premier essai en jeu (2026-10-13). Un fond de la couleur même, assombri, ne faisait que
  // remplacer le carré noir par un carré de couleur : la case redevenait une case.
  //
  // Sans fond et à l'ancienne largeur (0,48 de demi-largeur, 0,85 de demi-longueur), le papier
  // passait entre les touches en hachures blanches et le fond sombre du Vermeer sortait délavé.
  // Élargie à 0,66 et allongée à 0,95, la touche recouvre ses voisines et la feuille pleine se
  // referme à quelques points près ; seul prix, elle mord davantage sur les chiffres d'à côté.
  //
  // 0,80 et non plus 0,66 (2026-10-17) : ces « quelques points » trouaient un chaton noir, papier
  // crème sur noir, le contraste où ils se voient le plus. La touche s'effile aux bouts (0,26 à
  // 0,40 case de demi-largeur) et sa place tremble de ±0,15 case : deux voisines qui s'écartent
  // laissent passer le papier. Mesuré sur un aplat de 30 × 30 cases, part de papier visible :
  // 0,70 % en touches verticales — l'angle d'un aplat, sans gradient — et 0,84 % en horizontales
  // à 0,66 ; 0,04 % à 0,75 ; zéro à 0,80. Le prix d'hier ne se paie plus : la feuille n'a plus de
  // chiffres (bareSheet).
  const L = s * (0.95 + 0.25 * hash2(seed, 6));
  const W = s * 0.8;
  ctx.fillStyle = gradientOf(ctx, t, W);
  ctx.beginPath();
  ctx.moveTo(-L, -W * 0.6);
  ctx.quadraticCurveTo(0, -W * 1.1, L, -W * 0.4);
  ctx.quadraticCurveTo(L * 1.15, 0, L, W * 0.4);
  ctx.quadraticCurveTo(0, W * 1.1, -L, W * 0.6);
  ctx.quadraticCurveTo(-L * 1.1, 0, -L, -W * 0.6);
  ctx.fill();
  // Les poils du pinceau : à moins de 5 px de case ils ne se voient plus et coûtent autant que
  // la touche elle-même — on s'en passe.
  if (s >= 5) {
    ctx.strokeStyle = t.bristle;
    ctx.globalAlpha *= 0.35;
    ctx.lineWidth = s * 0.03;
    ctx.beginPath();
    for (let k = -2; k <= 2; k++) {
      ctx.moveTo(-L * 0.9, k * W * 0.2);
      ctx.lineTo(L * 0.9, k * W * 0.18 + (hash2(seed, k + 9) - 0.5) * W * 0.2);
    }
    ctx.stroke();
  }
  ctx.restore();
}

export const peinture: Alphabet = {
  id: 'peinture',
  label: 'Touches de peinture',
  symbolName: 'couleur',
  selfColored: true,
  helpText:
    'Une touche de pinceau par case, vingt-quatre couleurs. La touche se couche toute seule le ' +
    'long des formes, et les couleurs voisines se mélangent à l’œil comme sur une toile.',

  aspect: () => 1,
  sheetMode: 'single',

  // DU PLUS CLAIR AU PLUS SOMBRE, et dans cet ordre-là sans choix (orderedPlay) : les sombres
  // se posent en dernier, donc PAR-DESSUS.
  //
  // C'était l'inverse, « comme on monte une peinture, les ombres d'abord ». Mesuré le 2026-10-13
  // en rendant le tableau fini dans sept ordres de pose (écart OKLab ×100 à l'original, moyenne
  // par case, 120 cases), sur cinq sujets :
  //
  //                      Vermeer  Frida  Van Gogh  Cri   Eiffel
  //   sombres d'abord     17,6    11,1    11,1    20,9   17,1
  //   ligne par ligne     14,8     9,0     8,2    18,4   13,5
  //   couleurs au hasard  13,5–16,1  8,7–9,3  7,8–8,3  17,2–18,0  13,8–14,2
  //   clairs d'abord      12,6     8,0     7,8    16,7   11,4
  //
  // Une touche couvre deux cases et demie : la couleur posée en dernier mange ses voisines. Les
  // clairs en dernier délavaient tout — le visage du Vermeer s'aplatissait, yeux et bouche
  // perdus dans la chair ; les sombres en dernier redessinent les contours, les narines, les
  // paupières. Trié sur la clarté mesurée et non sur l'ordre des codes, que les six teintes
  // ajoutées en fin de liste ne suivent pas.
  //
  // SAUF LES DEUX BLANCS, posés EN DERNIER (2026-10-14). Commencer une feuille par du blanc sur
  // un papier crème, c'est ouvrir la partie sur la couleur qui se voit le moins — « bizarre de
  // commencer par la peinture blanche ». Remesuré sur douze images (mêmes conditions, 120 cases,
  // fond exclu), le tableau y GAGNE : 5,57 de moyenne dans l'ordre des clartés, 5,22 avec le
  // blanc seul à la fin, 5,25 avec les deux blancs. Ce sont les rehauts — le reflet de la perle,
  // l'éclat d'un œil, un col — que les couleurs posées après venaient manger ; posés en dernier,
  // ils restent dessus, comme les derniers éclats d'un peintre. Gagnant sur neuf images sur
  // douze ; seul Le Cri y perd nettement (4,87 → 5,73). Les DEUX plutôt que le blanc seul, au
  // même score à 0,03 près : la partie s'ouvre alors sur la chair claire, une couleur qui se voit.
  paletteOrder: (() => {
    const byLightness = PAINTS.map((_, i) => i + 1).sort((a, b) => CODE_L[b] - CODE_L[a]);
    const whites = byLightness.slice(0, 2);
    return [...byLightness.slice(2), ...whites.reverse()];
  })(),
  orderedPlay: true,
  // Ni grille ni numéros (demandé le 2026-10-13, une fois l'ordre imposé) : la grille rayait la
  // toile sous des touches qui débordent de 0,6 case, et le chiffre ne servait plus à choisir.
  bareSheet: true,

  codeName: (code) => PAINTS[code - 1]?.name ?? '',

  prepareGrid(target, originX = 0, originY = 0) {
    angles.prepare(target, originX, originY);
  },

  draw(ctx, x, y, w, h, code) {
    if (code < 1 || code > PAINTS.length) return;
    stroke(ctx, x, y, w, h, TONES[code - 1], angles.at(x, y, w, h));
  },

  swatch(ctx, w, h, code) {
    const t = TONES[code - 1];
    if (t) paintSwatch(ctx, w, h, t);
  },
};

/** La pastille : une touche entière dans son carré, rognée — débordante elle mordait sur la
 *  pastille voisine. Posée sur un fond de sa couleur assombrie : la touche n'a plus de fond à
 *  elle (cf. stroke), et sans lui le papier ressortait en coin crème dans chaque pastille. */
export function paintSwatch(ctx: CanvasRenderingContext2D, w: number, h: number, t: Tone): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, w, h);
  ctx.clip();
  ctx.fillStyle = t.dark;
  ctx.fillRect(0, 0, w, h);
  stroke(ctx, 0, 0, w, h, t, DEFAULT_ANGLE);
  ctx.restore();
}

/**
 * Ce que chaque code rend VRAIMENT, papier et débords compris, en OKLab — pour que
 * l'encodeur apparie contre la case peinte et non contre la couleur pure. Mesuré comme au banc :
 * le code pavé 5 × 5 sur lui-même, la case du centre lue, et ses voisines y débordent comme sur
 * une vraie feuille.
 */
let measured: [number, number, number][] | null = null;
export function measuredPaints(): [number, number, number][] {
  if (!measured) measured = measureTones(TONES);
  return measured;
}

/** La mesure elle-même, pour n'importe quel nuancier — `peintureNuancee` mesure ses 256. À
 *  l'angle par défaut et non à celui de la dernière feuille ouverte : la mesure ne doit pas en
 *  dépendre. */
export function measureTones(tones: readonly Tone[]): [number, number, number][] {
  const S = 30;
  const n = 5;
  const c = document.createElement('canvas');
  c.width = c.height = S * n;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  const out: [number, number, number][] = [];
  for (const t of tones) {
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, S * n, S * n);
    for (let r = 0; r < n; r++)
      for (let q = 0; q < n; q++) stroke(ctx, q * S, r * S, S, S, t, DEFAULT_ANGLE);
    const d = ctx.getImageData(2 * S, 2 * S, S, S).data;
    let R = 0;
    let G = 0;
    let B = 0;
    // moyenne en lumière linéaire : c'est ainsi que l'œil mélange une case vue de loin
    for (let i = 0; i < d.length; i += 4) {
      R += s2l(d[i]);
      G += s2l(d[i + 1]);
      B += s2l(d[i + 2]);
    }
    const m = d.length / 4;
    out.push(srgbToOklab(l2s(R / m), l2s(G / m), l2s(B / m)));
  }
  return out;
}
